import React from 'react'
import { AppForms } from '@/app'
import { AppStatus } from '@/redux'
import { APIClient } from '@/services'
import { AnyFunction, useForm } from '@codeleap/common'
import { Replace, TypeGuards } from '@codeleap/types'
import { Organisation } from '@/features'

type SanitizedProfile = Replace<APIClient.Session.Profile, 'organization', Organisation.T.Organisation['id']>

export function useAnswerProfile(options: { onSuccess?: AnyFunction } = {}) {
  const { onSuccess } = options

  const updateProfile = APIClient.Session.useUpdateProfile()
  const { data: profile } = APIClient.Session.useProfile()

  const form = useForm(AppForms.editProfile, {
    initialState: { ...profile }
  })

  const onUpdate = React.useCallback(
    async (key: keyof SanitizedProfile) => {
      const value = form.values[key]
      if (!value) return

      const payload: SanitizedProfile = { ...(profile as unknown as SanitizedProfile), [key]: value }
      if (key === 'organization' && TypeGuards.isObject(value)) {
        payload.organization = (value as Organisation.T.Organisation).id
      }

      try {
        AppStatus.set('loading')

        await updateProfile.mutateAsync(payload as SanitizedProfile)
        APIClient.Session.QueryKeys.me.refresh()

        onSuccess?.()

        AppStatus.set('done')
      } catch (error) {
        logger.error('Error updating profile', error, `Profile Prompt - ${key}`)
      } finally {
        AppStatus.set('idle')
      }
    },
    [form, profile]
  )

  const isAnswered = React.useCallback(
    (key: keyof SanitizedProfile) => {
      const answer = profile[key]
      if (Array.isArray(answer)) return answer.length > 0
      return !TypeGuards.isNil(answer)
    },
    [profile]
  )

  return { onUpdate, isAnswered, form }
}

export type UseAnswerProfile = ReturnType<typeof useAnswerProfile>
